import { z } from "zod";

import {
  busbarMaterialSchema,
  busbarSelectionStatusSchema,
  busbarSelectionWarningCodeSchema,
  checkKeySchema,
  checkStatusSchema,
  faultTypeSchema,
  materialChoiceSchema,
  prospectiveCurrentSourceSchema,
  referencePartSchema,
  shortCircuitCaseSchema,
  systemKindSchema,
  temperatureBasisSchema,
  temperatureRiseMethodSchema,
  verificationOutcomeSchema,
  voltageDropBasisSchema,
} from "./busbarSelectionContract";
import {
  calculationRunDetailSchema,
  calculationRunListResponseSchema,
  calculationRunSummarySchema,
  type CalculationRunDetail,
  type CalculationRunListResponse,
} from "./calculationRun";
import { jurisdictionProfileSchema } from "./cableContract";
import { exactDecimalSchema } from "./faultContract";
import { CHOOSE_A_FAULT_RUN } from "./faultLink";
import { ApiError, describeApiError, requestJson } from "./http";

const RUNS_URL = "/api/v1/electrical/busbar-selection/runs";

const UNEXPECTED = "Unexpected response from the KES Electrical OS busbar selection runs API.";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const optionalText = (maxLength: number) => z.string().trim().max(maxLength).optional();

const requiredText = (maxLength: number) => z.string().trim().min(1).max(maxLength);

// A fault level typed by the engineer: allowed, but not linked evidence, so the study is
// REVIEW_REQUIRED. Without the peak ip the peak check is not made.
export const typedFaultLevelSchema = z
  .object({
    prospective_current_ka: exactDecimalSchema,
    peak_current_ka: exactDecimalSchema.optional(),
    system_nominal_voltage_v: exactDecimalSchema,
    fault_bus_code: requiredText(50),
  })
  .strict();

export const arrangementRequestSchema = z
  .object({
    bars: z.number().int().min(1),
    width_mm: exactDecimalSchema,
    thickness_mm: exactDecimalSchema,
  })
  .strict();

// Where the evidence is written. All three parts verify its values; a blank or missing part
// is not judged here: the backend holds the values as not verified (founder rule 2026-10-01).
export const evidenceReferenceRequestSchema = z
  .object({
    document: optionalText(200),
    revision_or_date: optionalText(100),
    page_or_clause: optionalText(100),
  })
  .strict();

export const deratingFactorRequestSchema = z
  .object({
    name: requiredText(100),
    value: exactDecimalSchema,
    source: optionalText(500),
    reference: evidenceReferenceRequestSchema.optional(),
  })
  .strict();

export const shortCircuitEvidenceRequestSchema = z
  .object({
    icw_ka: exactDecimalSchema.optional(),
    icw_duration_s: exactDecimalSchema.optional(),
    ipk_ka: exactDecimalSchema.optional(),
    reference: evidenceReferenceRequestSchema.optional(),
  })
  .strict();

export const ampacityEvidenceRequestSchema = z
  .object({
    base_ampacity_a: exactDecimalSchema.optional(),
    base_ampacity_source: optionalText(500),
    derating_factors: z.array(deratingFactorRequestSchema).max(20).optional(),
    base_ampacity_at_site_conditions: z.boolean().optional(),
    site_conditions_source: optionalText(500),
    reference: evidenceReferenceRequestSchema.optional(),
  })
  .strict();

export const temperatureRiseRequestSchema = z
  .object({
    method: temperatureRiseMethodSchema.optional(),
    reference_design: optionalText(500),
    verified_rated_current_a: exactDecimalSchema.optional(),
    ambient_basis_c: exactDecimalSchema.optional(),
    temperature_basis: temperatureBasisSchema.optional(),
    temperature_value: exactDecimalSchema.optional(),
    permissible_criterion_value: exactDecimalSchema.optional(),
    verification_document: optionalText(500),
    reference: evidenceReferenceRequestSchema.optional(),
  })
  .strict();

export const supportRequestSchema = z
  .object({
    support_spacing_mm: exactDecimalSchema.optional(),
    phase_spacing_mm: exactDecimalSchema.optional(),
    support_type: optionalText(200),
    manufacturer_reference: optionalText(500),
    declared_max_support_spacing_mm: exactDecimalSchema.optional(),
    declared_peak_ka: exactDecimalSchema.optional(),
    reference: evidenceReferenceRequestSchema.optional(),
  })
  .strict();

export const neutralRequestSchema = z
  .object({
    has_neutral: z.boolean(),
    proposed_percent_of_phase: exactDecimalSchema.optional(),
    harmonic_data_available: z.boolean().optional(),
    calculated_neutral_current_a: exactDecimalSchema.optional(),
    neutral_rating_a: exactDecimalSchema.optional(),
    reference: evidenceReferenceRequestSchema.optional(),
  })
  .strict();

export const protectiveEarthRequestSchema = z
  .object({
    earth_fault_current_ka: exactDecimalSchema,
    clearing_time_s: exactDecimalSchema,
    pe_area_mm2: exactDecimalSchema,
    k_factor: exactDecimalSchema.optional(),
    k_source: optionalText(500),
    reference: evidenceReferenceRequestSchema.optional(),
  })
  .strict();

export const clearanceRequestSchema = z
  .object({
    phase_phase: verificationOutcomeSchema.optional(),
    phase_earth: verificationOutcomeSchema.optional(),
    creepage: verificationOutcomeSchema.optional(),
    support_short_circuit_strength: verificationOutcomeSchema.optional(),
    reference: evidenceReferenceRequestSchema.optional(),
  })
  .strict();

export const buswayRowRequestSchema = z
  .object({
    rated_current_a: exactDecimalSchema,
    material: busbarMaterialSchema,
    icw_ka: exactDecimalSchema,
    icw_duration_s: exactDecimalSchema,
    ipk_ka: exactDecimalSchema,
    voltage_drop_mv_per_a_m: exactDecimalSchema,
    source: optionalText(500),
    voltage_drop_basis: voltageDropBasisSchema.optional(),
    reference: evidenceReferenceRequestSchema.optional(),
  })
  .strict();

export const buswayRequestSchema = z
  .object({
    rows: z.array(buswayRowRequestSchema).min(1).max(200),
    route_length_m: exactDecimalSchema,
    allowable_voltage_drop_percent: exactDecimalSchema,
  })
  .strict();

// Only the two structural rules of the request are checked here: the fault level comes
// from exactly one place, and a fault run id has the shape of an id. The engineering rules
// (bars per phase, the form of the design current, busway data for a BUSWAY study, and the
// rest) live in the backend engine, the single source of truth; its message is shown as the
// backend words it.
export const busbarSelectionRequestSchema = z
  .object({
    code: requiredText(50),
    name: requiredText(200),
    bus_code: requiredText(50),
    // The bus of the single-line diagram the study is about; left out, it is not stored.
    sld_element_code: requiredText(50).optional(),
    // Exactly one of the two: a stored EOS-04 fault run of the organization, or a typed value.
    fault_run_id: z.string().optional(),
    typed: typedFaultLevelSchema.optional(),
    required_fault_duration_s: exactDecimalSchema,
    system_kind: systemKindSchema.optional(),
    material: materialChoiceSchema,
    max_bars_per_phase: z.number().int().optional(),
    design_current_a: exactDecimalSchema.optional(),
    load_current_a: exactDecimalSchema.optional(),
    margin_factor: exactDecimalSchema.optional(),
    selected_arrangement: arrangementRequestSchema.optional(),
    site_ambient_c: exactDecimalSchema.optional(),
    short_circuit: shortCircuitEvidenceRequestSchema.optional(),
    ampacity: ampacityEvidenceRequestSchema.optional(),
    temperature_rise: temperatureRiseRequestSchema.optional(),
    supports: supportRequestSchema.optional(),
    neutral: neutralRequestSchema.optional(),
    protective_earth: protectiveEarthRequestSchema.optional(),
    clearance: clearanceRequestSchema.optional(),
    busway: buswayRequestSchema.optional(),
    jurisdiction_profile: jurisdictionProfileSchema.optional(),
    notes: optionalText(1000),
  })
  .strict()
  .superRefine((values, ctx) => {
    if ((values.fault_run_id === undefined) === (values.typed === undefined)) {
      ctx.addIssue({
        code: "custom",
        message: "give exactly one prospective current source: fault_run_id or typed",
        path: ["fault_run_id"],
      });
    } else if (values.fault_run_id !== undefined && !UUID.test(values.fault_run_id)) {
      ctx.addIssue({ code: "custom", message: CHOOSE_A_FAULT_RUN, path: ["fault_run_id"] });
    }
  });

export const busbarSelectionRunCreateRequestSchema = z
  .object({
    study: busbarSelectionRequestSchema,
    project_revision_id: z.string().uuid().optional(),
    notes: optionalText(2000),
  })
  .strict();

export const arrangementResponseSchema = z
  .object({
    bars: z.number().int(),
    width_mm: z.string(),
    thickness_mm: z.string(),
    area_mm2: z.string(),
  })
  .strict();

export const materialSelectionResponseSchema = z
  .object({
    material: busbarMaterialSchema,
    current_density_a_mm2: z.string(),
    required_area_mm2: z.string(),
    arrangement: arrangementResponseSchema.nullable(),
    installed_area_mm2: z.string().nullable(),
    utilisation_percent: z.string().nullable(),
  })
  .strict();

export const evidenceReferenceResponseSchema = z
  .object({
    document: z.string().nullable(),
    revision_or_date: z.string().nullable(),
    page_or_clause: z.string().nullable(),
  })
  .strict();

export const buswayRowResponseSchema = z
  .object({
    rated_current_a: z.string(),
    material: busbarMaterialSchema,
    icw_ka: z.string(),
    icw_duration_s: z.string(),
    ipk_ka: z.string(),
    voltage_drop_mv_per_a_m: z.string(),
    source: z.string().nullable(),
    voltage_drop_basis: voltageDropBasisSchema.nullable(),
    reference: evidenceReferenceResponseSchema.nullable(),
  })
  .strict();

export const buswaySelectionResponseSchema = z
  .object({
    material: busbarMaterialSchema,
    row: buswayRowResponseSchema.nullable(),
    utilisation_percent: z.string().nullable(),
  })
  .strict();

// A value a check used without a complete document reference: "Data not verified". The path
// is the value's place in the request, the value is as entered (unit null for a text value),
// and missing names the blank parts of its reference.
export const unverifiedValueSchema = z
  .object({
    path: z.string(),
    label: z.string(),
    value: z.string(),
    unit: z.string().nullable(),
    missing: z.array(referencePartSchema).min(1),
  })
  .strict();

// The stored status, the word of KES-EL-BUS-002 section 17 (PASS, REVIEW_REQUIRED, FAILED,
// NOT_APPLICABLE), the reason, and the values the check used that are not verified.
export const checkResponseSchema = z
  .object({
    number: z.number().int(),
    key: checkKeySchema,
    title: z.string(),
    status: checkStatusSchema,
    outcome: z.string(),
    reason: z.string(),
    unverified: z.array(unverifiedValueSchema),
  })
  .strict();

export const busbarSelectionWarningSchema = z
  .object({
    code: busbarSelectionWarningCodeSchema,
    message: z.string(),
  })
  .strict();

export const busbarSelectionResponseSchema = z
  .object({
    code: z.string(),
    name: z.string(),
    bus_code: z.string(),
    system_kind: systemKindSchema,
    material: materialChoiceSchema,
    current_source: prospectiveCurrentSourceSchema,
    // The linked run, its case, fault type and bus as the stored result holds them; null run /
    // case / type for a typed value. ip is null when it was not held.
    fault_run_id: z.string().uuid().nullable(),
    calculation_case: shortCircuitCaseSchema.nullable(),
    fault_type: faultTypeSchema.nullable(),
    fault_bus_code: z.string(),
    prospective_current_ka: z.string(),
    peak_current_ka: z.string().nullable(),
    system_nominal_voltage_v: z.string(),
    required_fault_duration_s: z.string(),
    design_current_a: z.string(),
    // The load current and the margin factor the design current was built from; both null
    // when the design current was entered directly.
    load_current_a: z.string().nullable(),
    margin_factor: z.string().nullable(),
    max_bars_per_phase: z.number().int().nullable(),
    selections: z.array(materialSelectionResponseSchema),
    busway_selections: z.array(buswaySelectionResponseSchema),
    evaluated_arrangement: arrangementResponseSchema.nullable(),
    evaluated_installed_area_mm2: z.string().nullable(),
    evaluated_utilisation_percent: z.string().nullable(),
    effective_capacity_a: z.string().nullable(),
    pe_required_area_mm2: z.string().nullable(),
    voltage_drop_v: z.string().nullable(),
    voltage_drop_percent: z.string().nullable(),
    checks: z.array(checkResponseSchema),
    status: busbarSelectionStatusSchema,
    warnings: z.array(busbarSelectionWarningSchema),
    data_set_name: z.string(),
    data_set_basis_sha256: z.string().length(64),
    jurisdiction_profile: jurisdictionProfileSchema,
    notes: z.string().nullable(),
  })
  .strict();

// The controlled data set as the run stored it when it was calculated: a later change of the
// basis document never rewrites it (backend schemas/calculation_run.py,
// BusbarSelectionDataSet).
export const busbarSelectionDataSetSchema = z
  .object({
    name: z.string(),
    source_document: z.string(),
    revision: z.string(),
    approval_status: z.string(),
    approved_on: z.string(),
    basis_file: z.string(),
    basis_sha256: z.string().length(64),
  })
  .strict();

export const busbarSelectionRunResponseSchema = z
  .object({
    run: calculationRunSummarySchema,
    result: busbarSelectionResponseSchema,
    data_set: busbarSelectionDataSetSchema,
  })
  .strict();

export type TypedFaultLevel = z.infer<typeof typedFaultLevelSchema>;
export type BusbarSelectionRequest = z.infer<typeof busbarSelectionRequestSchema>;
export type BusbarSelectionResponse = z.infer<typeof busbarSelectionResponseSchema>;
export type UnverifiedValue = z.infer<typeof unverifiedValueSchema>;
export type BusbarSelectionDataSet = z.infer<typeof busbarSelectionDataSetSchema>;
export type BusbarSelectionRunCreateRequest = z.infer<typeof busbarSelectionRunCreateRequestSchema>;
export type BusbarSelectionRunResponse = z.infer<typeof busbarSelectionRunResponseSchema>;

export type BusbarSelectionRunFilter = {
  calculation_key?: string;
  project_revision_id?: string;
  limit?: number;
};

function refusal(data: unknown, status: number, fallback: string): ApiError {
  return new ApiError(describeApiError(data, status, fallback), status);
}

function parse<T>(schema: z.ZodType<T>, data: unknown): T {
  const parsed = schema.safeParse(data);

  if (!parsed.success) {
    throw new Error(UNEXPECTED);
  }

  return parsed.data;
}

/**
 * Calculate a busbar selection study and persist it as a run revision.
 *
 * With a project revision id the run is stored under that revision; without one it belongs
 * to no project. An unchanged study returns the stored revision with HTTP 200 instead of a
 * new one (A11); both are valid here. The actor is never sent: the server takes it from the
 * session.
 */
export async function createBusbarSelectionRun(
  payload: BusbarSelectionRunCreateRequest,
  signal?: AbortSignal,
): Promise<BusbarSelectionRunResponse> {
  const response = await requestJson(RUNS_URL, {
    method: "POST",
    body: busbarSelectionRunCreateRequestSchema.parse(payload),
    signal,
  });

  if (!response.ok) {
    throw refusal(response.data, response.status, "The busbar selection run was not saved");
  }

  return parse(busbarSelectionRunResponseSchema, response.data);
}

/** The runs of one scope, newest first; a study code and a project revision narrow the list. */
export async function listBusbarSelectionRuns(
  filter: BusbarSelectionRunFilter = {},
  signal?: AbortSignal,
): Promise<CalculationRunListResponse> {
  const query = new URLSearchParams();

  if (filter.calculation_key !== undefined) {
    query.set("calculation_key", filter.calculation_key);
  }
  if (filter.project_revision_id !== undefined) {
    query.set("project_revision_id", filter.project_revision_id);
  }
  if (filter.limit !== undefined) {
    query.set("limit", String(filter.limit));
  }

  const text = query.toString();
  const response = await requestJson(text === "" ? RUNS_URL : `${RUNS_URL}?${text}`, {
    method: "GET",
    signal,
  });

  if (!response.ok) {
    throw refusal(response.data, response.status, "The busbar selection runs could not be loaded");
  }

  return parse(calculationRunListResponseSchema, response.data);
}

/** One stored run with its frozen snapshots. */
export async function getBusbarSelectionRun(
  runId: string,
  signal?: AbortSignal,
): Promise<CalculationRunDetail> {
  const response = await requestJson(`${RUNS_URL}/${encodeURIComponent(runId)}`, {
    method: "GET",
    signal,
  });

  if (!response.ok) {
    throw refusal(response.data, response.status, "The busbar selection run could not be loaded");
  }

  return parse(calculationRunDetailSchema, response.data);
}
